import React, { useState, useRef, useEffect } from "react";
import { ClickAwayListener } from "@mui/material";

import useWindowDimensions from "@/main/hooks/useKogWindowDimension";
import { isEmpty } from "@/main/services/utils";
import { ResolutionsEnum } from "@/main/services/enum/ResolutionsEnum";

import TooltipComponent, { TypePlacementTooltip } from "../tooltip/tooltipComponent";
import { createPopperAnchor, initializeMouseMoveListener } from "./utilsRenderTooltipMessage";
import * as S from "./styles";
import { handleKeyActivate } from "@/main/services/utils";

interface IRenderTooltipMessageProps {
	tooltipMessage?: string;
	placement?: TypePlacementTooltip;
	followCursor?: boolean;
	withClick?: boolean;
	isWidthChildren?: boolean;
	isAnchoredToChild?: boolean;
	style?: React.CSSProperties;
}

function RenderTooltipMessage({
	tooltipMessage,
	placement = "top",
	withClick = false,
	followCursor = false,
	isWidthChildren = false,
	isAnchoredToChild = false,
	style,
	children,
}: React.PropsWithChildren<IRenderTooltipMessageProps>) {
	const { width } = useWindowDimensions();
	const [isOpenTooltip, setIsOpenTooltip] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const positionRef = useRef({ x: 0, y: 0 });
	const animationFrame = useRef<number | null>(null);

	useEffect(() => {
		return initializeMouseMoveListener(followCursor, positionRef, setPosition, animationFrame);
	}, [followCursor]);

	const propsFollowCursor = createPopperAnchor({
		followCursor,
		x: position.x,
		y: position.y,
	});
	const isMobile = width < ResolutionsEnum.desktop;

	if (isEmpty(tooltipMessage)) return <>{children}</>;

	if (withClick && isMobile) {
		return (
			<ClickAwayListener onClickAway={() => setIsOpenTooltip(false)}>
				<div>
					<TooltipComponent
						placement={placement}
						title={<S.ContainerTitle>{tooltipMessage}</S.ContainerTitle>}
						onClick={() => setIsOpenTooltip((prev) => !prev)}
						open={isOpenTooltip}
						style={style}
					>
						<div
							role="button"
							tabIndex={0}
							onKeyDown={handleKeyActivate(() => setIsOpenTooltip((prev) => !prev))}
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								setIsOpenTooltip((prev) => !prev);
							}}
						>
							<S.AreaChildrenTooltip $isWidthChildren={isWidthChildren} style={{ pointerEvents: "none" }}>
								{children}
							</S.AreaChildrenTooltip>
						</div>
					</TooltipComponent>
				</div>
			</ClickAwayListener>
		);
	}

	return (
		<TooltipComponent
			placement={placement}
			title={<S.ContainerTitle>{tooltipMessage}</S.ContainerTitle>}
			style={style}
			{...propsFollowCursor}
		>
			{isAnchoredToChild && React.isValidElement(children) ? (
				children
			) : (
				<S.AreaChildrenTooltip $isWidthChildren={isWidthChildren}>{children}</S.AreaChildrenTooltip>
			)}
		</TooltipComponent>
	);
}

export default RenderTooltipMessage;
